export type ItemType = "Product" | "Category" | "Brand" | "Pet" | "Service";

export type ContentType =
  | "Product"
  | "Category"
  | "Brand"
  | "Pet"
  | "Service"
  | "Video"
  | "Custom";

export type ScreenDefinition = {
  id: number;
  name: string;
};

export type SectionManagerType = "Items" | "Banner";

export type SectionDefinition = {
  value: string;
  label: string;

  /**
   * Existing item type.
   * Kept for backward compatibility.
   */
  itemType?: ItemType;

  /**
   * Generic content model.
   */
  supportedContentTypes?: ContentType[];

  /**
   * Defines which admin screen manages this section.
   */
  managerType?: SectionManagerType;

  configurable?: boolean;
};

export const SCREEN_DEFINITIONS: ScreenDefinition[] = [
  {
    id: 1,
    name: "Products",
  },
  {
    id: 2,
    name: "Pets",
  },
  {
    id: 3,
    name: "Services",
  },
];

export const SECTION_DEFINITIONS: Record<string, SectionDefinition[]> = {
  // ============================================================
  // PRODUCTS
  // ============================================================

  Products: [
    {
      value: "MainCarousel",
      label: "Main Carousel",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "CategorySlideshow",
      label: "Category Slideshow",
      itemType: "Category",
      supportedContentTypes: ["Category", "Custom"],
      configurable: true,
    },

    {
      value: "ProductCarouselWithOffer",
      label: "Product Carousel With Offer",
      itemType: "Product",
      supportedContentTypes: ["Product", "Custom"],
      configurable: true,
    },

    {
      value: "PeekCarousel",
      label: "Peek Carousel",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "StripBanner",
      label: "Strip Banner",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "BrandsSlider",
      label: "Brands Slider",
      itemType: "Brand",
      supportedContentTypes: ["Brand", "Custom"],
      configurable: true,
    },

    {
      value: "PetPromoBanner",
      label: "Pet Promo Banner",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "WavesSection",
      label: "Waves Section",
      itemType: "Product",
      supportedContentTypes: ["Product", "Custom"],
      configurable: true,
    },

    {
      value: "CategorySlider",
      label: "Category Slider",
      itemType: "Category",
      supportedContentTypes: ["Category", "Custom"],
      configurable: true,
    },

    {
      value: "MultiBanner",
      label: "Multi Banner",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "ProductCarouselWithoutOffer",
      label: "Product Carousel Without Offer",
      itemType: "Product",
      supportedContentTypes: ["Product", "Custom"],
      configurable: true,
    },

    {
      value: "PetBanner",
      label: "Pet Banner",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "VideoBanners",
      label: "Product Video Banners",
      itemType: "Product",
      supportedContentTypes: ["Product", "Video", "Custom"],
      configurable: true,
    },

    {
      value: "FeatureHighlights",
      label: "Feature Highlights",
    },
  ],

  // ============================================================
  // PETS
  // ============================================================

  Pets: [
    {
      value: "MainCarousel",
      label: "Main Carousel",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "PetCarouselDogs",
      label: "Dogs",
      itemType: "Pet",
      supportedContentTypes: ["Pet", "Custom"],
      configurable: true,
    },

    {
      value: "VideoCarouselPets",
      label: "Pet Videos",
      itemType: "Pet",
      supportedContentTypes: ["Pet", "Video", "Custom"],
      configurable: true,
    },

    {
      value: "PetCarouselCats",
      label: "Cats",
      itemType: "Pet",
      supportedContentTypes: ["Pet", "Custom"],
      configurable: true,
    },

    {
      value: "HappyParentsSection",
      label: "Happy Parents",
    },

    {
      value: "ReviewSection",
      label: "Reviews",
    },

    {
      value: "PetBannerFurry",
      label: "Furry Pet Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "AdoptSection",
      label: "Adoption",
      itemType: "Pet",
      supportedContentTypes: ["Pet", "Custom"],
      configurable: true,
    },

    {
      value: "ConsultationBanner",
      label: "Consultation Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "HowAdoptionWorks",
      label: "How Adoption Works",
    },

    {
      value: "NewsletterBox",
      label: "Newsletter",
    },
  ],

  // ============================================================
  // SERVICES
  // ============================================================

  Services: [
    {
      value: "MainCarousel",
      label: "Main Carousel",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "ServicesCategorySection",
      label: "Services",
      itemType: "Service",
      supportedContentTypes: ["Service","Custom"],
      configurable: true,
    },

    {
      value: "PetTrainingExperience",
      label: "Pet Training Experience",
      configurable: false,
    },

    {
      value: "PromoBanner",
      label: "Promo Banner",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "PetGroomingService",
      label: "Pet Grooming",
      itemType: "Service",
      supportedContentTypes: ["Service"],
      configurable: true,
    },

    {
      value: "WellnessBanner",
      label: "Wellness Banner",
      managerType: "Banner",
      supportedContentTypes: ["Custom"],
      configurable: true,
    },

    {
      value: "TrendingProducts",
      label: "Trending Products",
      itemType: "Product",
      supportedContentTypes: ["Product"],
      configurable: true,
    },
  ],
};

// ============================================================
// GET SECTION DEFINITION
// ============================================================

export const getSectionDefinition = (
  sectionType: string,
  screenName?: string,
): SectionDefinition | undefined => {
  /**
   * When screen name is provided, search only
   * inside that screen.
   *
   * This is important because the same section
   * type can exist in multiple screens.
   *
   * Example:
   *
   * Products -> MainCarousel
   * Pets     -> MainCarousel
   */
  if (screenName) {
    const screenSections = SECTION_DEFINITIONS[screenName];

    return screenSections?.find(
      (x: SectionDefinition) => x.value === sectionType,
    );
  }

  /**
   * Backward-compatible fallback.
   */
  return Object.values(SECTION_DEFINITIONS)
    .flat()
    .find((x: SectionDefinition) => x.value === sectionType);
};

// ============================================================
// DEFAULT CONFIGURATION
// ============================================================

/**
 * Default ConfigJson for every configurable section.
 */
export const getDefaultConfig = (sectionType: string): Record<string, any> => {
  switch (sectionType) {
    case "ProductCarouselWithOffer":
      return {
        ItemCount: 10,
        CategoryId: null,
        BrandId: null,
        OnlyInStock: true,
        ShowOffer: true,
        TrendingOnly: false,
        SortBy: null,
        SourceType: "Dynamic",
      };

    case "ProductCarouselWithoutOffer":
      return {
        ItemCount: 10,
        CategoryId: null,
        BrandId: null,
        OnlyInStock: true,
        ShowOffer: false,
        TrendingOnly: false,
        SortBy: null,
        SourceType: "Dynamic",
      };

    case "TrendingProducts":
      return {
        ItemCount: 10,
        OnlyInStock: true,
        TrendingOnly: true,
        SortBy: null,
        SourceType: "Dynamic",
      };

    case "WavesSection":
      return {
        Title: "⚡ Best Selling Toys ⚡",
        ItemCount: 10,
        CategoryId: null,
        BrandId: null,
        OnlyInStock: true,
        TrendingOnly: false,
        SortBy: null,
        SourceType: "Dynamic",
      };

    case "VideoBanners":
      return {
        ItemCount: 10,
        SourceType: "Dynamic",
      };

    case "CategorySlider":
      return {
        ItemCount: 10,
        SourceType: "Dynamic",
      };

    case "CategorySlideshow":
      return {
        ItemCount: 10,
        ParentCategoryId: null,
        Level: null,
        SourceType: "Dynamic",
      };

    case "BrandsSlider":
      return {
        ItemCount: 10,
        SourceType: "Dynamic",
      };

    case "PetCarouselDogs":
    case "PetCarouselCats":
    case "AdoptSection":
    case "VideoCarouselPets":
      return {
        ItemCount: 10,
        SourceType: "Dynamic",
      };

    case "ServicesCategorySection":
    case "PetGroomingService":
      return {
        ItemCount: 10,
        SourceType: "Dynamic",
      };

    default:
      return {};
  }
};

// ============================================================
// SOURCE TYPE SECTIONS
// ============================================================

/**
 * Sections which have SourceType configuration.
 *
 * SourceType is separate from ContentType.
 *
 * ContentType:
 *   Product / Category / Brand / Pet / Service / Video / Custom
 *
 * SourceType:
 *   Dynamic / Manual
 */
export const SOURCE_SECTIONS = [
  "ProductCarouselWithOffer",
  "ProductCarouselWithoutOffer",
  "TrendingProducts",
  "WavesSection",
  "VideoBanners",
  "BrandsSlider",
  "CategorySlider",
  "CategorySlideshow",
  "PetCarouselDogs",
  "PetCarouselCats",
  "AdoptSection",
  "VideoCarouselPets",
  "ServicesCategorySection",
  "PetGroomingService",
];

// ============================================================
// SUPPORTED CONTENT TYPES
// ============================================================

/**
 * Returns the content types supported by a section builder.
 *
 * The screen name should be provided whenever possible
 * because the same section type can exist in multiple screens.
 *
 * Example:
 *
 * getSupportedContentTypes(
 *   "MainCarousel",
 *   "Products",
 * );
 *
 * getSupportedContentTypes(
 *   "MainCarousel",
 *   "Pets",
 * );
 */
export const getSupportedContentTypes = (
  sectionType: string,
  screenName?: string,
): ContentType[] => {
  const definition = getSectionDefinition(sectionType, screenName);

  if (!definition) {
    return [];
  }

  /**
   * New content model.
   */
  if (definition.supportedContentTypes?.length) {
    return definition.supportedContentTypes;
  }

  /**
   * Backward compatibility with the
   * existing itemType configuration.
   */
  if (definition.itemType) {
    return [definition.itemType];
  }

  return [];
};
